import React, { FC, useCallback, useState } from 'react'
import {
  Paper,
  Typography,
  Theme,
  Button,
  Dialog,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardMedia
} from '@material-ui/core'
import { makeStyles, createStyles, useTheme } from '@material-ui/styles'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import { useMutation, useQuery } from 'react-apollo'
import {
  PW_ACCOUNT_DISPLAYS_SUBSCRIPTION,
  PW_WORKSPACE_SUBSCRIPTION_AND_DISPLAYS
} from 'src/apollo/api/paywall/queries'
import { UPDATE_DISPLAY_SUBSCRIPTION } from 'src/apollo/api/paywall/mutations'
import { RemotePriceTable /*, PriceTable*/ } from 'src/ui'
import { HtmlTooltip } from '../atoms/html-tooltip'
import { StripeStatusEnums } from '../../screenhub-shared/enum/stripe.enum'
import { isActiveSubscription } from '../../helpers/paywallHelper'
import { WorkspaceRoles } from 'src/constants'
import { useUserInfo } from 'src/services'
import { getCurrentWorkspaceFromLocalStorage } from 'src/helpers/local-storage'
import { history } from 'src/app'
import { isHideFeature } from '../../helpers/white-label-helper'
import { WhiteLabelFeaturesEnum } from '../../helpers/white-label.enum'
import urlHelper from '../../helpers/urlHelper'
import useWorkspaceId from '../../hooks/useWorkspaceId'
import { screenhubColor } from '../../styles/color'
import firstDisplayBanner from '../assets/first-display-banner.png'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      // ...theme.typography.button,
      // backgroundColor: theme.palette.background.paper,
      // padding: theme.spacing(1),
    }
  })
)

const usePaperClasses = makeStyles<Theme>({
  root: {
    // XXX: shall combine whole color rendered with theme
    backgroundColor: '#DEEFFC',
    padding: '0 30px',
    width: '100%',
    minHeight: 32,
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    zIndex: 5,
    justifyContent: 'center',
    whiteSpace: 'nowrap',
    boxShadow: 'none'
  }
})

const useFirstDisplayStyles = makeStyles({
  root: {
    Width: 550
  },
  media: {
    height: 140
  }
})

export const TrialBanner: FC = () => {
  const workspaceId = useWorkspaceId()
  const theme = useTheme()
  const classes = useStyles()
  const firstDisplayClasses = useFirstDisplayStyles()
  const paperClasses = usePaperClasses(theme)

  const {
    me: {
      account: { id: accountId }
    }
  } = useUserInfo()

  const memberRole = getCurrentWorkspaceFromLocalStorage()?.memberRole

  const { data, loading, refetch } = useQuery(PW_ACCOUNT_DISPLAYS_SUBSCRIPTION, {
    variables: {
      accountId
    }
  })

  const {
    refetch: workspaceSubscriptionAndDisplaysDataRefetch
  } = useQuery(PW_WORKSPACE_SUBSCRIPTION_AND_DISPLAYS, {
    skip: !workspaceId,
    variables: {
      workspaceId
    }
  })

  const [updateSubscription] = useMutation(UPDATE_DISPLAY_SUBSCRIPTION)

  const subscription = data?.getLatestSubscription
  const displays = data?.getDisplays
  const showBanner =
    subscription?.status !== StripeStatusEnums.ACTIVE && memberRole?.name === WorkspaceRoles.OWNER
  const [showPriceTable, setShowPriceTable] = useState(false)
  const [showFirstDisplay, setShowFirstDisplay] = useState(false)
  const handlePriceTableClose = () => {
    setShowPriceTable(false)
  }

  const handleCheckoutClick = async () => {
    if (
      subscription.status === StripeStatusEnums.INCOMPLETE ||
      subscription.status === StripeStatusEnums.INCOMPLETE_EXPIRED ||
      subscription.status === StripeStatusEnums.PAST_DUE ||
      subscription.status === StripeStatusEnums.UNPAID
    ) {
      // go to billing page
      history.push(urlHelper.getBillingPageUrl(workspaceId))
    } else if (
      subscription.status === StripeStatusEnums.TRIALING ||
      subscription.status === StripeStatusEnums.CANCELED
    ) {
      setShowPriceTable(true)
    }
  }

  const remoteDialogCallback = event => {
    const { eventName, data } = event.data
    if (
      eventName === 'closeRemoteManageDisplayQuantityDialog' &&
      data?.status === 'done' &&
      refetch &&
      workspaceSubscriptionAndDisplaysDataRefetch
    ) {
      refetch();
      workspaceSubscriptionAndDisplaysDataRefetch();
    }
  };

  window.addEventListener('message', remoteDialogCallback)

  const handleNextClick = useCallback(async () => {
    await updateSubscription({
      variables: {
        accountId,
        quantity: 1,
        cirrusStandardQty: 0
      }
    })

    refetch()
    workspaceSubscriptionAndDisplaysDataRefetch()

    history.replace({
      pathname: location.pathname,
      search: '?action=create-display'
    })
  }, [
    updateSubscription,
    accountId,
    refetch,
    workspaceSubscriptionAndDisplaysDataRefetch,
    history,
    location
  ])

  const trialEnd = subscription?.trialEnd || subscription?.currentPeriodEnd || Date.now() / 1000
  const daysToExpiry = Math.ceil((trialEnd - Date.now() / 1000) / 86400)
  const isTrialingOrActive = isActiveSubscription(subscription) && trialEnd > 0
  const expiryWarning = isTrialingOrActive && daysToExpiry > 0 && daysToExpiry <= 7
  // trial banner background depends following conditions:
  // more than 7 days to expiry
  // 7 days to expiry
  // expired,
  const bannerWording = () => {
    switch (subscription.status) {
      case StripeStatusEnums.TRIALING:
        if (!isTrialingOrActive) {
          return (
            <>
              <Typography className={classes.root}>Your subscription has expired!</Typography>
              <Button color="primary" component="div" onClick={handleCheckoutClick}>
                CLICK HERE
              </Button>
              <Typography>to checkout.&nbsp;</Typography>
            </>
          )
        } else {
          return (
            <>
              <Typography className={classes.root}>
                {daysToExpiry} days left on your free trial.
              </Typography>
              <Button color="primary" component="div" onClick={handleCheckoutClick}>
                CLICK HERE
              </Button>
              <Typography>to checkout.&nbsp;</Typography>
            </>
          )
        }
      case StripeStatusEnums.PAST_DUE:
      case StripeStatusEnums.UNPAID:
      case StripeStatusEnums.INCOMPLETE:
      case StripeStatusEnums.INCOMPLETE_EXPIRED:
      case StripeStatusEnums.CANCELED:
        return (
          <>
            <Typography className={classes.root}>Your authorization has expired.</Typography>
            <Button color="primary" component="div" onClick={handleCheckoutClick}>
              CLICK HERE
            </Button>
            <Typography>to check the details.&nbsp;</Typography>
          </>
        )
    }
  }

  const tooltipWording = () => {
    if (!isTrialingOrActive) {
      return `Your screens can no longer be connected, but none of your work is lost. Continue to checkout to reactivate your account!`
    } else {
      return `We hope you're enjoying ScreenHub! Checkout your plan before your trial ends to continue creating and publishing content.`
    }
  }

  const isHidePaywall = isHideFeature(WhiteLabelFeaturesEnum.PAYWALL)

  if (loading || isHidePaywall) {
    return <></>
  }

  // banner for 1st time user
  if (!subscription) {
    return (
      <Paper
        classes={paperClasses}
        elevation={1}
        square
        style={{
          backgroundColor: screenhubColor.blue[100],
          fontSize: 16,
          height: 48
        }}
      >
        Begin your 30-day trial by adding your first display.🚀&nbsp;
        <Button
          variant="text"
          onClick={() => setShowFirstDisplay(true)}
          style={{ color: screenhubColor.blue[500] }}
        >
          Add Display
        </Button>
        <Dialog open={showFirstDisplay} onClose={() => setShowFirstDisplay(false)}>
          <Card className={firstDisplayClasses.root}>
            <CardActionArea>
              <CardMedia
                className={firstDisplayClasses.media}
                image={firstDisplayBanner}
                title="Create your first display"
              />
              <CardContent>
                <Typography gutterBottom variant="h5" component="h2">
                  Enjoy a full 30-day trial of our standard plan!
                </Typography>
                <ul>
                  <li>No credit card required - add your first display and start exploring</li>
                  <li>We'll remind you 7 days before your trial ends</li>
                  <li>Access all ScreenHub standard features during your trial</li>
                  <li>
                    Experience the power of ScreenHub and how simple it is to own a digital sign
                  </li>
                </ul>
              </CardContent>
            </CardActionArea>
            <CardActions
              style={{
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <Button variant="contained" color="primary" onClick={handleNextClick}>
                Next
              </Button>
            </CardActions>
          </Card>
        </Dialog>
      </Paper>
    )
  }

  return showBanner ? (
    <Paper
      classes={paperClasses}
      elevation={1}
      square
      style={{
        backgroundColor: !isTrialingOrActive ? '#F9DEDC' : expiryWarning ? '#F8EBC8' : '#DEEFFC'
      }}
    >
      {bannerWording()}
      <HtmlTooltip
        title={
          <React.Fragment>
            <Typography style={{ fontSize: 14, fontWeight: 400 }}>{tooltipWording()}</Typography>
            <Typography style={{ display: 'flex', alignItems: 'center', justifyContent: 'right' }}>
              <Button onClick={handleCheckoutClick} style={{ color: '#FFFFFF' }}>
                UPGRADE
              </Button>
            </Typography>
          </React.Fragment>
        }
        interactive={true}
        leaveDelay={100}
        placement="top"
      >
        <InfoOutlinedIcon />
      </HtmlTooltip>
      <Dialog maxWidth="lg" open={showPriceTable} onClose={handlePriceTableClose}>
        {/*<PriceTable
          subscription={subscription}
          displays={displays}
          closeDialog={handlePriceTableClose}
        />*/}
        <RemotePriceTable onClose={handlePriceTableClose} />
      </Dialog>
    </Paper>
  ) : (
    <></>
  )
}
